import type { QueryClient } from '@tanstack/react-query';
import type { ProjectFonotekaTrack, ProjectFonotekaTracks } from '@/api/client';
import { patchProjectFonotekaTracksInCache } from './cache';
import { projectFonotekaTracksKey } from './queryKeys';

/**
 * Треки проекта в карточке: свои правки, ответы сервера и realtime сходятся в один список без моргания — как сохранение
 * полей проекта (useProjectSave): правка сразу в кэше, на время своих запросов эхо realtime не применяется, ответ ложится
 * только последним запросом, при ошибке откатывается только своя правка.
 *
 * Эхо: на каждое добавление сервер шлёт всем project:fonotekaTracksChanged со списком на тот момент. При двух быстрых галочках
 * эхо первой ([a]) приходит, когда в кэше уже [a, b], — применённое, оно сняло бы вторую галочку. Поэтому пока свои запросы
 * в пути и ECHO_QUIET_MS после последнего — события откладываются, а затем применяется последнее из них: сервер шлёт их
 * по порядку, так что это его итоговый список (с чужими правками за это время). Совпал с кэшем — ничего не меняется.
 */

/** Как DEDUP_MS у project:updated (projectRealtimeDedup): эхо своего запроса приходит не позже */
export const ECHO_QUIET_MS = 800;

interface ProjectSync {
  pending: number;
  quietTimer: ReturnType<typeof setTimeout> | null;
  /** Последнее отложенное событие — итоговый список сервера */
  deferredIds: string[] | null;
}

const syncs = new Map<string, ProjectSync>();

function syncOf(projectId: string): ProjectSync {
  let s = syncs.get(projectId);
  if (!s) {
    s = { pending: 0, quietTimer: null, deferredIds: null };
    syncs.set(projectId, s);
  }
  return s;
}

function settle(queryClient: QueryClient, projectId: string, s: ProjectSync): void {
  if (s.quietTimer) clearTimeout(s.quietTimer);
  s.quietTimer = setTimeout(() => {
    s.quietTimer = null;
    if (s.pending > 0) return;
    const ids = s.deferredIds;
    syncs.delete(projectId);
    if (ids) patchProjectFonotekaTracksInCache(queryClient, projectId, ids);
  }, ECHO_QUIET_MS);
}

/** realtime project:fonotekaTracksChanged: свои запросы в пути или только что закончились — отложить, иначе применить */
export function receiveProjectFonotekaTracksEvent(queryClient: QueryClient, projectId: string, trackIds: string[]): void {
  const s = syncs.get(projectId);
  if (s) {
    s.deferredIds = trackIds;
    return;
  }
  patchProjectFonotekaTracksInCache(queryClient, projectId, trackIds);
}

export interface ProjectTracksChange {
  /** Правка поверх текущего списка — и для мгновенного показа, и для отката поверх того, что стало к ошибке */
  apply: (tracks: readonly ProjectFonotekaTrack[]) => ProjectFonotekaTrack[];
  revert: (tracks: readonly ProjectFonotekaTrack[]) => ProjectFonotekaTrack[];
  request: () => Promise<ProjectFonotekaTracks>;
}

/** Правка треков проекта; null — сохранено, иначе ошибка (откат уже сделан, показать её — дело вызывающего) */
export async function writeProjectFonotekaTracks(
  queryClient: QueryClient,
  projectId: string,
  change: ProjectTracksChange
): Promise<unknown> {
  const key = projectFonotekaTracksKey(projectId);
  const s = syncOf(projectId);
  s.pending++;
  if (s.quietTimer) {
    clearTimeout(s.quietTimer);
    s.quietTimer = null;
  }
  const current = () => queryClient.getQueryData<ProjectFonotekaTracks>(key)?.tracks ?? [];
  const put = (tracks: ProjectFonotekaTrack[]) => patchProjectFonotekaTracksInCache(queryClient, projectId, tracks.map((t) => t.id), tracks);
  try {
    // Чтение списка, начатое до правки, вернуло бы в кэш прежний список
    await queryClient.cancelQueries({ queryKey: key, exact: true });
    put(change.apply(current()));
    const res = await change.request();
    // Ответ раннего запроса — без правок, сделанных следом: ложится только последний
    if (s.pending === 1) patchProjectFonotekaTracksInCache(queryClient, projectId, res.track_ids, res.tracks);
    return null;
  } catch (err) {
    put(change.revert(current()));
    return err ?? new Error('request failed');
  } finally {
    s.pending--;
    if (s.pending === 0) settle(queryClient, projectId, s);
  }
}

/** Для тестов: забыть отложенное между тестами */
export function resetProjectFonotekaSyncForTests(): void {
  for (const s of syncs.values()) if (s.quietTimer) clearTimeout(s.quietTimer);
  syncs.clear();
}
